import { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import { useAuth as useClerkAuth } from "@clerk/react";
import api from "@/lib/api";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const { isLoaded, isSignedIn, signOut } = useClerkAuth();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const hasResolved = useRef(false);

  // Hard timeout: NEVER wait more than 4 seconds for Clerk.
  // In Capacitor (capacitor://localhost origin), Clerk may never fire isLoaded.
  // After 4s we stop waiting and let the app render the auth page.
  useEffect(() => {
    const timer = setTimeout(() => {
      if (!hasResolved.current) {
        hasResolved.current = true;
        setLoading(false);
      }
    }, 4000);
    return () => clearTimeout(timer);
  }, []);

  const refresh = useCallback(async () => {
    const mobileToken = typeof window !== "undefined" ? localStorage.getItem("tanta_mobile_token") : null;
    const guestToken = typeof window !== "undefined" ? localStorage.getItem("tanta_guest_token") : null;
    // If neither mobile token, guest token, nor Clerk signed-in, resolve as logged-out.
    if (!isSignedIn && !guestToken && !mobileToken) {
      setUser(null);
      if (!hasResolved.current) {
        hasResolved.current = true;
        setLoading(false);
      }
      return null;
    }
    try {
      if (isSignedIn) {
        // Try to get a Clerk token (short timeout: 3s max)
        try {
          const deadline = Date.now() + 3000;
          while (Date.now() < deadline) {
            if (window.Clerk?.session) {
              const token = await window.Clerk.session.getToken();
              if (token) break;
            } else if (typeof window.Clerk?.getToken === "function") {
              const token = await window.Clerk.getToken();
              if (token) break;
            }
            await new Promise((r) => setTimeout(r, 150));
          }
        } catch (_) { /* ignore token errors */ }
      }

      const { data } = await api.get("/auth/me");
      setUser(data);

      // Sync language preference
      try {
        const lang = localStorage.getItem("tanta.lang");
        if (lang && data && data.lang !== lang) {
          api.post("/users/me/lang", { lang }).catch(() => {});
        }
      } catch (_) { /* ignore */ }

      return data;
    } catch (err) {
      if (err?.response?.status === 401) {
        if (typeof window !== "undefined") {
          localStorage.removeItem("tanta_guest_token");
          localStorage.removeItem("tanta_mobile_token");
        }
        setUser(null);
      }
      return null;
    } finally {
      if (!hasResolved.current) {
        hasResolved.current = true;
        setLoading(false);
      }
    }
  }, [isSignedIn]);

  // Run refresh once Clerk loads OR once it times out.
  useEffect(() => {
    if (isLoaded) {
      refresh();
    }
  }, [isLoaded, isSignedIn, refresh]);

  // Global session-expiry handler
  useEffect(() => {
    const onExpired = async () => {
      if (typeof window !== "undefined") {
        localStorage.removeItem("tanta_guest_token");
        localStorage.removeItem("tanta_mobile_token");
      }
      setUser(null);
      try { await signOut(); } catch (_) {}
      if (typeof window !== "undefined") {
        const p = window.location.pathname;
        if (p !== "/" && !p.startsWith("/auth")) {
          window.location.replace("/auth");
        }
      }
    };
    window.addEventListener("tanta:session-expired", onExpired);
    return () => window.removeEventListener("tanta:session-expired", onExpired);
  }, [signOut]);

  const loginAsGuest = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/auth/guest");
      if (data?.token) {
        localStorage.setItem("tanta_guest_token", data.token);
        setUser(data.user);
        return data.user;
      }
    } catch (err) {
      console.error("Guest login failed:", err);
      throw err;
    } finally {
      if (!hasResolved.current) {
        hasResolved.current = true;
      }
      setLoading(false);
    }
  };

  const logout = async () => {
    if (typeof window !== "undefined") {
      localStorage.removeItem("tanta_guest_token");
      localStorage.removeItem("tanta_mobile_token");
    }
    try { await signOut(); } catch (_) {}
    setUser(null);
  };

  const login = async (email, password) => {
    setLoading(true);
    try {
      const { data } = await api.post("/auth/mobile/login", { email, password });
      if (data?.token) {
        localStorage.setItem("tanta_mobile_token", data.token);
        setUser(data.user);
        return data.user;
      }
    } finally {
      if (!hasResolved.current) {
        hasResolved.current = true;
      }
      setLoading(false);
    }
  };

  const register = async (formData) => {
    setLoading(true);
    try {
      const { data } = await api.post("/auth/mobile/signup", formData);
      if (data?.token) {
        localStorage.setItem("tanta_mobile_token", data.token);
        setUser(data.user);
        return data.user;
      }
    } finally {
      if (!hasResolved.current) {
        hasResolved.current = true;
      }
      setLoading(false);
    }
  };

  const googleSession = async () => {};
  const googleNative = async () => {};

  return (
    <AuthCtx.Provider value={{ user, setUser, loading, login, register, loginAsGuest, googleSession, googleNative, logout, refresh }}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
